// display_mode resolution — pure, unit-tested, NO stored resolution.
// Order: user override → building shared default → auto heuristic.
// Auto heuristic (§2.4): single tenant → consolidated;
// multi-tenant OR any pinned member → split.
//
// Changing any of these inputs NEVER touches billing data — presentation only.

import type { BuildingDisplayMode, ResolvedDisplayMode } from './types';

export interface DisplayModeInputs {
  userOverride: ResolvedDisplayMode | null | undefined;
  buildingDefault: BuildingDisplayMode | null | undefined;
  tenantCount: number;
  hasPinnedMember: boolean;
}

export function autoDisplayMode(
  tenantCount: number,
  hasPinnedMember: boolean,
): ResolvedDisplayMode {
  if (tenantCount > 1 || hasPinnedMember) return 'split';
  return 'consolidated';
}

export function resolveDisplayMode(inputs: DisplayModeInputs): {
  mode: ResolvedDisplayMode;
  source: 'user' | 'building' | 'auto';
} {
  const { userOverride, buildingDefault, tenantCount, hasPinnedMember } = inputs;
  if (userOverride === 'consolidated' || userOverride === 'split') {
    return { mode: userOverride, source: 'user' };
  }
  if (buildingDefault === 'consolidated' || buildingDefault === 'split') {
    return { mode: buildingDefault, source: 'building' };
  }
  return { mode: autoDisplayMode(tenantCount, hasPinnedMember), source: 'auto' };
}

// Suite parser — display only. Pulls "Suite 200" / "Ste 120" / "#101" out of a
// single-string facility_address. Returns null when nothing suite-like found.
export function parseSuite(address: string | null | undefined): string | null {
  if (!address) return null;
  const m = address.match(/\b(?:suite|ste\.?|unit)\s*#?\s*([A-Za-z0-9-]+)/i)
    ?? address.match(/#\s*([A-Za-z0-9-]+)/);
  return m ? m[1].toUpperCase() : null;
}
